import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { ReviewsTrackClick, SelectedElementType } from 'components/Accommodation/DateGuestInput/ReviewsTrackClick';
import { toggle } from 'opticks';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Box } from '@daedalus/atlas/helpers/Box';
import { SkeletonLoader } from '@daedalus/atlas/SkeletonLoader';
import { Text } from '@daedalus/atlas/Text';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getLanguageCode } from '@daedalus/core/src/localization/services/locale';
import { useGetUserReviewsQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { ReviewRating } from '@daedalus/shared/src/search/ReviewRating';
import { ReviewSentiments } from '@daedalus/shared/src/search/ReviewSentiments';
import { ContentHotel, Review } from '@findhotel/sapi/dist/types/packages/core/src';
import { ReviewSorting } from '../../Reviews/ReviewSorting';
import { AP_DESKTOP_LARGE_COLUMN_WIDTH, AP_DESKTOP_OVERLAY_LARGE_COLUMN_WIDTH } from '../AccommodationView/Desktop/constant';
import { GuestReviewLayoutConfig, layoutConfigMapping } from './config';
import { GuestReviewLayout } from './constants';
import { GuestReviews } from './GuestReviews';
import { GuestReviewsLoader } from './GuestReviewsLoader';
import { GuestTypesSubSection } from './GuestTypesSubSection';
import { ReviewCarouselSection } from './ReviewCarouselSection';
import { ReviewsSectionLoadingState } from './ReviewSectionLoadingState';
import { SummarySubsection } from './SummarySubSections';
import { getReviewAvailability } from './utils';
const _exp2 = /*#__PURE__*/() => ({
  hasGuestReviews,
  mountedOnOverlay
}) => hasGuestReviews && mountedOnOverlay ? '100vh' : 'auto';
const _exp3 = /*#__PURE__*/() => ({
  hasGuestReviews
}) => hasGuestReviews ? `'rating comments'
           'summary comments'` : `'rating summary'
           'rating summary'`;
const _exp6 = /*#__PURE__*/() => ({
  mountedOnOverlay
}) => `${mountedOnOverlay ? AP_DESKTOP_OVERLAY_LARGE_COLUMN_WIDTH : AP_DESKTOP_LARGE_COLUMN_WIDTH}px`;
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "ww39det",
  propsAsIs: false,
  vars: {
    "ww39det-0": [_exp2()],
    "ww39det-1": [_exp3()],
    "ww39det-2": [_exp6()]
  }
});
const ReviewsRatingWrapper = /*#__PURE__*/styled('div')({
  name: "ReviewsRatingWrapper",
  class: "r19vpczs",
  propsAsIs: false
});
const ReviewsSummaryWrapper = /*#__PURE__*/styled('div')({
  name: "ReviewsSummaryWrapper",
  class: "r1nxnovy",
  propsAsIs: false
});
const ReviewCommentsWrapper = /*#__PURE__*/styled('div')({
  name: "ReviewCommentsWrapper",
  class: "r1funwz8",
  propsAsIs: false
});
interface Props {
  anchorRef: React.RefObject<HTMLDivElement>;
  hotel?: ContentHotel;
  layoutConfig?: GuestReviewLayout;
  mountedOnOverlay?: boolean;
}
export const ReviewsSection: React.FC<Props> = ({
  hotel,
  anchorRef,
  layoutConfig = GuestReviewLayout.DEFAULT,
  mountedOnOverlay = false
}) => {
  const {
    isMobile,
    isDesktopXs
  } = useDeviceLayout();
  const isMobileOrTablet = isMobile || isDesktopXs;
  const dispatchTeamEvent = useDispatchTeamEvent();
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const {
    brandIsVio
  } = useBrand();
  const languageCode = useSelector(getLanguageCode);
  const isEnglishLanguage = languageCode.startsWith('en');
  const isGuestReviewsLanguageBSide = toggle('f72fa251-guest-reviews-languages', false, true);
  const hasGuestReviews = isGuestReviewsLanguageBSide && brandIsVio || !isGuestReviewsLanguageBSide && brandIsVio && isEnglishLanguage;
  const hotelId = hotel?.objectID;
  const reviewLayout = layoutConfigMapping[layoutConfig];
  const [offset, setOffset] = useState(0);
  const [storedReviews, setStoredReviews] = useState<Review[]>([]);
  const [noMoreReviews, setNoMoreReviews] = useState(false);
  // added as part of 9633c72c-guest-reviews-sort-filter experiment
  const [sortBy, setSortBy] = useState(null);
  const {
    data,
    isLoading: isReviewsRequestLoading,
    isFetching: isReviewRequestFetching
  } = useGetUserReviewsQuery({
    hotelId,
    language: languageCode,
    pageSize: 10,
    offset,
    sortBy
  }, {
    skip: !hasGuestReviews || !hotel
  });
  useEffect(() => {
    if (data?.nextOffset < 1 || !isGuestReviewsLanguageBSide && data?.reviews?.find(review => review.language !== 'en')) {
      setNoMoreReviews(true);
    }
    if (data?.reviews?.length > 0) {
      setStoredReviews(prev => [...prev, ...data.reviews]);
    }
  }, [data, isGuestReviewsLanguageBSide]);
  const reviewsData = useMemo(() => {
    if (isGuestReviewsLanguageBSide) return storedReviews;
    return storedReviews.filter(review => review.language === 'en');
  }, [storedReviews, isGuestReviewsLanguageBSide]);
  useEffect(() => {
    if (inView) {
      dispatchTeamEvent(trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsReviewsSection,
        action: Action.Viewed
      }));
    }
  }, [dispatchTeamEvent, inView]);
  const {
    hasAnyReviewComponent,
    hasReviews,
    hasSentiments,
    hasGuestsTypes,
    hasSummary
  } = getReviewAvailability(hotel);
  const handleReviewSortChange = useCallback((value: string) => {
    if (value !== sortBy) {
      // reset list
      setStoredReviews([]);
      // reset offset
      setOffset(0);
      // update sort
      setSortBy(value);
    }
  }, [sortBy]);
  if (!hotel) {
    return <div data-id="reviews-loader">
        <Box paddingBottom="s500">
          <Text variant={isMobile ? 'titleL' : 'titleXL'}>
            <FormattedMessageWrapper id="reviews" defaultMessage="Reviews" />
          </Text>
        </Box>
        <ReviewsSectionLoadingState />
      </div>;
  }
  if (!hasAnyReviewComponent) return null;
  const shouldShowDivider = !isMobile && hasGuestsTypes && hasSummary && !hasGuestReviews;

  // the components are config driven, in the AP page we will show the review carousel
  // and in the reviews overlay we will show all the guest reviews instead of carousel
  const renderComponent = (id: GuestReviewLayoutConfig) => {
    switch (id) {
      case GuestReviewLayoutConfig.ReviewsRating:
        return <ReviewsRatingWrapper key="leftWrapper">
            {!mountedOnOverlay && <Text variant={isMobile ? 'titleL' : 'titleXL'}>
                <FormattedMessageWrapper id="reviews" defaultMessage="Reviews" />
              </Text>}
            {hasReviews && <ReviewsTrackClick dataId="reviews-subsection" element={SelectedElementType.Rating} entity={Entity.HotelDetailsReviewsSection}>
                <ReviewRating rating={hotel.guestRating.overall} totalRatingCount={hotel.reviewCount} hotelId={hotel.objectID} />
              </ReviewsTrackClick>}
            {hasSentiments && <ReviewsTrackClick dataId="sentiments-subsection" element={SelectedElementType.Sentiment} entity={Entity.HotelDetailsReviewsSection}>
                <ReviewSentiments sentiments={hotel.sentiments} />
              </ReviewsTrackClick>}
          </ReviewsRatingWrapper>;
      case GuestReviewLayoutConfig.ReviewComments:
        if (mountedOnOverlay) {
          return <ReviewCommentsWrapper>
              {isMobile && <Box paddingBottom="s700">
                  <Divider />
                </Box>}
              <Box marginBottom="s600" display="flex" alignItems="center" justifyContent="space-between">
                <Text variant="titleL">
                  <FormattedMessageWrapper id="guestReviews" defaultMessage="What guests say" />
                </Text>
                <ReviewSorting onChange={handleReviewSortChange} />
              </Box>
              {reviewsData.length == 0 && (isReviewRequestFetching || isReviewsRequestLoading) ? <GuestReviewsLoader /> : <GuestReviews key="reviewComments" mountedOnOverlay reviewsData={reviewsData} noMoreReviews={noMoreReviews} onLoadMore={() => setOffset(data?.nextOffset)} />}
            </ReviewCommentsWrapper>;
        }
        if (reviewsData?.length > 0) {
          return <ReviewCommentsWrapper>
              {isMobile && <Box paddingBottom="s700">
                  <Divider />
                </Box>}
              <Box marginBottom="s600" display="flex" alignItems="center" justifyContent="space-between">
                <Text variant="titleS">
                  <FormattedMessageWrapper id="guestReviews" defaultMessage="What guests say" />
                </Text>
              </Box>
              <GuestReviews key="reviewComments" reviewsData={reviewsData} noMoreReviews={noMoreReviews} onLoadMore={() => setOffset(data?.nextOffset)} />
            </ReviewCommentsWrapper>;
        }
        return null;
      case GuestReviewLayoutConfig.ReviewsCarousel:
        if (isReviewsRequestLoading) return <SkeletonLoader height={250} />;
        return <ReviewCarouselSection reviewsData={reviewsData} key="reviewsCarousel" />;
      case GuestReviewLayoutConfig.ReviewsSummary:
        return <ReviewsSummaryWrapper key="rightWrapper" className={cx(hasGuestReviews && '__hasGuestReviews')}>
            {hasGuestsTypes && <ReviewsTrackClick dataId="guestTypes-subsection" element={SelectedElementType.GuestType} entity={Entity.HotelDetailsReviewsSection}>
                <GuestTypesSubSection guestType={hotel.guestType} />
              </ReviewsTrackClick>}
            {shouldShowDivider && <Divider />}
            {hasSummary && <div data-id="summary-subsection">
                <SummarySubsection guestRating={hotel?.guestRating} />
              </div>}
          </ReviewsSummaryWrapper>;
      default:
        return null;
    }
  };
  return <div data-id="reviews-section" ref={anchorRef}>
      <Wrapper ref={ref} className={cx(!isMobileOrTablet && '__isDesktop')} hasGuestReviews={hasGuestReviews} mountedOnOverlay={mountedOnOverlay}>
        {reviewLayout.map(id => renderComponent(id))}
      </Wrapper>
    </div>;
};

require("./ReviewsSection.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ReviewsSection.tsx");